import React, { useState } from 'react';
import { toast } from 'sonner';
import { Star } from 'lucide-react';
import { cn, API_BASE_URL } from '../../lib/utils';

/**
 * Same star rating + POST /customer/ai-chat/app-rating as AI chat (direct chat / hybrid thread).
 */
export default function AppRatingInline({
  eventId,
  token,
  disabled,
  existingAccountRating,
  onRated,
  variant = 'light',
}) {
  const [hover, setHover] = useState(0);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const saved =
    typeof existingAccountRating === 'number' &&
    existingAccountRating >= 1 &&
    existingAccountRating <= 5;

  const submit = async (stars) => {
    if (busy || done || saved || !token) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/customer/ai-chat/app-rating`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stars, event_id: eventId || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Could not save rating');
      setSelected(stars);
      setDone(true);
      onRated?.();
      if (data.open_store && data.store_url) {
        window.open(data.store_url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      toast.error(e.message || 'Could not save rating');
    } finally {
      setBusy(false);
    }
  };

  const filledUpTo = done && selected != null ? selected : saved ? existingAccountRating : null;
  const readOnly = saved && !done;
  const isOnWhite = variant === 'light';

  return (
    <div
      className={cn('mt-3 pt-3 border-t', isOnWhite ? 'border-violet-100' : 'border-white/20')}
      data-testid="direct-chat-app-rating"
    >
      <p
        className={cn(
          'text-xs font-medium mb-2',
          isOnWhite ? 'text-violet-900' : 'text-white/90',
        )}
      >
        {readOnly ? 'Your rating' : 'Tap a star to rate the app'}
      </p>
      <div className="flex gap-0.5 items-center">
        {[1, 2, 3, 4, 5].map((n) =>
          readOnly ? (
            <span key={n} className="p-1" aria-hidden>
              <Star
                className={cn(
                  'w-6 h-6 sm:w-7 sm:h-7 transition-colors',
                  filledUpTo != null && filledUpTo >= n
                    ? 'fill-amber-400 text-amber-500'
                    : isOnWhite
                      ? 'text-violet-200'
                      : 'text-white/35',
                )}
              />
            </span>
          ) : (
            <button
              key={n}
              type="button"
              disabled={disabled || busy || done}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              onClick={() => submit(n)}
              className={cn(
                'p-1 rounded-lg transition-colors disabled:opacity-40',
                isOnWhite ? 'hover:bg-violet-100' : 'hover:bg-card/10',
              )}
              aria-label={`${n} stars`}
            >
              <Star
                className={cn(
                  'w-6 h-6 sm:w-7 sm:h-7 transition-colors',
                  (hover >= n || (filledUpTo != null && filledUpTo >= n))
                    ? 'fill-amber-400 text-amber-500'
                    : isOnWhite
                      ? 'text-violet-200'
                      : 'text-white/35',
                )}
              />
            </button>
          ),
        )}
      </div>
      {readOnly && (
        <p className={cn('text-xs mt-2', isOnWhite ? 'text-muted-foreground' : 'text-white/70')}>
          Thanks for your feedback!
        </p>
      )}
      {done && selected != null && (
        <p
          className={cn(
            'text-xs mt-2 font-medium',
            isOnWhite ? 'text-emerald-600' : 'text-emerald-200',
          )}
        >
          Thanks! You rated {selected} star{selected !== 1 ? 's' : ''}.
          {selected >= 4
            ? ' If the store did not open, you can rate us from the App Store or Play Store.'
            : ''}
        </p>
      )}
    </div>
  );
}
